import { Loader2 } from "lucide-react";

import { usePrivacyMasking } from "@/components/common/Sensitive";
import { useKaiChat } from "./KaiChatContext";
import { KaiClarificationCard } from "./KaiClarificationCard";
import { KaiElicitationCard } from "./KaiElicitationCard";
import { KaiHomeLayoutReviewCard } from "./KaiHomeLayoutReviewCard";
import { hasActiveClarification } from "./clarificationFieldUtils";
import { KaiMessageBubble, KaiWorkingBubble } from "./KaiMessageBubble";
import { KaiPlanProgress } from "./KaiPlanProgress";
import { KaiToolGroup } from "./KaiToolGroup";
import {
  isPlanFinalizingPhase,
  planFinalizingStatusMessage,
} from "./kaiPlanHelpers";
import {
  kaiStreamingFinalizingHint,
  kaiToolGroupStatusMessage,
  kaiWorkingBubbleMessage,
} from "./kaiStatusHelpers";
import { KaiMarkdown } from "./kaiMarkdown";
import { KaiMessageActions } from "./KaiMessageActions";
import { useKaiMarkdownActions } from "./useKaiMarkdownActions";

/** Live plan, tools, streaming prose, and thinking bubble for the current turn. */
export function KaiLiveTurn() {
  const kai = useKaiChat();
  const mask = usePrivacyMasking();
  const markdownActions = useKaiMarkdownActions();

  if (!kai) return null;
  if (
    !kai.isGenerating &&
    kai.pendingClarifications.length === 0 &&
    kai.pendingElicitations.length === 0 &&
    !kai.homeLayoutReview
  ) {
    return null;
  }

  const {
    streamingContent,
    streamingArtifacts,
    toolActivity,
    mcpStatus,
    planProgress,
    pendingClarifications,
    pendingElicitations,
    respondToElicitation,
    homeLayoutReview,
    isGenerating,
  } = kai;

  const clarificationOpen = hasActiveClarification(pendingClarifications);
  const showThinking =
    isGenerating &&
    !streamingContent &&
    !clarificationOpen &&
    (!planProgress || isPlanFinalizingPhase(planProgress, true));

  const hint = kaiStreamingFinalizingHint({
    isGenerating,
    streamingContent,
    toolActivity,
  });

  return (
    <>
      {isGenerating && planProgress && <KaiPlanProgress spec={planProgress} />}

      {isGenerating && toolActivity.length > 0 && (
        <KaiToolGroup
          tools={toolActivity}
          live
          statusMessage={kaiToolGroupStatusMessage({
            mcpStatus,
            toolActivity,
          })}
        />
      )}

      {isGenerating && streamingContent && (
        <div className="group/msg flex flex-col items-start gap-0.5">
          <div className="glass-panel max-w-[min(100%,38rem)] rounded-2xl px-4 py-3 text-sm text-foreground/90">
            <KaiMarkdown
              text={streamingContent}
              ctx={{
                isStreaming: true,
                artifacts: streamingArtifacts,
                maskSensitive: mask,
                conversationId: kai.activeId,
                onSendFollowUp: markdownActions.onSendFollowUp,
                beforeNavigate: markdownActions.beforeNavigate,
                onReviewEmail: markdownActions.onReviewEmail,
              }}
              afterBody={
                hint ? (
                  <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Loader2 className="size-3 animate-spin" />
                    {hint}
                  </p>
                ) : undefined
              }
            />
          </div>
          <KaiMessageActions
            content={streamingContent}
            showFeedback={false}
            className="px-1 opacity-100 transition-opacity sm:opacity-0 sm:group-hover/msg:opacity-100 sm:focus-within:opacity-100"
          />
        </div>
      )}

      {showThinking && (
        <KaiWorkingBubble
          message={
            planProgress
              ? planFinalizingStatusMessage(planProgress, mcpStatus)
              : kaiWorkingBubbleMessage({ mcpStatus, toolActivity })
          }
        />
      )}

      {pendingClarifications.map((clarification) => (
        <KaiClarificationCard
          key={clarification.clarificationId}
          clarification={clarification}
          onSubmit={kai.submitClarification}
        />
      ))}

      {pendingElicitations.map((elicitation) => (
        <KaiElicitationCard
          key={elicitation.elicitationId}
          elicitation={elicitation}
          onRespond={respondToElicitation}
        />
      ))}

      {homeLayoutReview && (
        <KaiHomeLayoutReviewCard review={homeLayoutReview} />
      )}
    </>
  );
}

interface KaiFollowUpsProps {
  suggestions: string[];
  onSelect: (text: string) => void;
}

export function KaiFollowUps({ suggestions, onSelect }: KaiFollowUpsProps) {
  if (suggestions.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {suggestions.map((text, index) => (
        <button
          key={text}
          type="button"
          onClick={() => onSelect(text)}
          style={{ animationDelay: `${70 + index * 80}ms` }}
          className="rounded-full border border-border/60 bg-foreground/[0.03] px-3 py-1 text-xs text-foreground/80 transition-colors hover:bg-accent/40 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-400 motion-safe:[animation-fill-mode:both]"
        >
          {text}
        </button>
      ))}
    </div>
  );
}

/** Persisted thread items (messages, tool groups, completed plans). */
export function KaiThreadItems() {
  const kai = useKaiChat();
  if (!kai) return null;

  return (
    <>
      {kai.thread.map((item, index) => {
        if (item.kind === "plan") {
          return <KaiPlanProgress key={`plan-${index}`} spec={item.spec} />;
        }
        if (item.kind === "tool_group") {
          return (
            <KaiToolGroup key={`tools-${index}`} tools={item.tools} />
          );
        }
        return (
          <KaiMessageBubble
            key={item.message.id}
            message={item.message}
            conversationId={kai.activeId}
          />
        );
      })}
    </>
  );
}
